/**
 * THE TRADER SURFACE'S ENDPOINTS — the twenty-three routes of `/user/v1`, typed from `@kinetiq/wire`.
 *
 * ⚠️ RECONCILED AGAINST THE SERVER'S PUBLISHED CONTRACT, NOT AGAINST A DOCUMENT. `apps/web/lifecycle.md`
 * and `apps/api/lifecycle.md` §§18–20 describe a DIFFERENT surface — `/me/balances`, `/me/deposit`,
 * `/trade/order` with an `expect` block and a `minOutRaw` floor. The trader process that exists
 * (`apps/api/src/user/router.ts`) serves the routes below and none of those. **The CODE is the contract**;
 * the divergences are listed in this app's `todo.md` rather than papered over, because a client written to
 * the document would produce exactly the failure the shared wire package exists to prevent: a page that
 * loads, returns 200s and silently does nothing.
 *
 * `/auth/otp/*` WAS ON THAT DIVERGENCE LIST AND NO LONGER IS — the four account-creation routes were built
 * on 2026-08-14 and are the first block below. Everything else in the list is still absent.
 *
 * THREE RULES, each a defect this file makes unrepresentable:
 *
 *  1. **`owner` IS NEVER SENT.** The wire declares it optional and INERT — present only so a wrong wallet is a
 *     403 rather than a silent substitution — so the honest client omits it entirely. The wallet comes from
 *     the session; a field that is never populated cannot be mismatched.
 *  2. **No URL is built by concatenation.** Paths and params go to `request()` separately, so they are
 *     encoded exactly once.
 *  3. **Timeouts are per-route.** A quote that has not answered in 6 s is stale anyway; a submit is given
 *     room because abandoning the CALL does not abandon the TRANSACTION.
 */

import type {
  AuthWalletResponse,
  ChallengeResponse,
  DepositBuildRequest,
  DepositBuildResponse,
  IntentsResponse,
  OrderCancelConfirmRequest,
  OrderCancelConfirmResponse,
  OrderCancelResponse,
  OrderCreateRequest,
  OrderCreateResponse,
  OrderKind,
  OrdersResponse,
  OtpLoginRequest,
  OtpLoginResponse,
  OtpStartResponse,
  OtpVerifyRequest,
  OtpVerifyResponse,
  QuoteRequestBody,
  QuoteResponse,
  ReportRequest,
  SessionResponse,
  SubmitResponse,
  SwapBuildRequest,
  SwapBuildResponse,
  TransferBuildRequest,
  TransferBuildResponse,
  VaultChallengeResponse,
  VaultRegisterRequest,
  VaultResponse,
  WhoAmIResponse,
} from "@kinetiq/wire";
import { request } from "./http.ts";
import { rememberTriggerToken } from "./triggerToken.ts";

const QUOTE_TIMEOUT_MS = 6_000;
const BUILD_TIMEOUT_MS = 20_000;
const SUBMIT_TIMEOUT_MS = 30_000;
/** ⚠️ LONGER THAN THE SERVER'S OWN VENDOR BUDGET (45 s in `BROADCASTING_TIMEOUT_MS`) AND SHORTER THAN
 *  nginx's 60 s, because whoever times out first owns the answer — and only the SERVER can watch the
 *  deposit afterwards. MEASURED 2026-09-16: jupiter's trigger create did not answer within 15 s, then did
 *  not answer within 25 s, twice leaving a user staring at "we do not know". Nothing had moved either time.
 *  If the browser aborts first it learns nothing and can learn nothing; this way the refusal the user reads
 *  is the one the server wrote, and the server has already handed the deposit to the confirmer. */
const ORDER_CREATE_TIMEOUT_MS = 55_000;

// ── account creation ──────────────────────────────────────────────────────────────────────────────────
//
// All four are `auth: "none"` because they are what you call BEFORE you have a credential. The gate is not
// ours and is not skippable: reading the mailbox.

/** Sends mail. Bounded server-side per EMAIL (1/60s, burst 2) on top of the per-client bucket, so a `429`
 *  here is a legitimate "you just asked" and should be shown as one rather than retried. */
export function startEmailOtp(email: string): Promise<OtpStartResponse> {
  return request<OtpStartResponse>({
    base: "trade",
    path: "/auth/otp/start",
    method: "POST",
    auth: "none",
    body: { email },
  });
}

/** The bundle is SEALED — see `OtpVerifyRequest`. Every failure is one body on purpose; do not try to tell a
 *  wrong code from an expired one, because the server will not say and the user's move is the same. */
export function verifyEmailOtp(body: OtpVerifyRequest): Promise<OtpVerifyResponse> {
  return request<OtpVerifyResponse>({
    base: "trade",
    path: "/auth/otp/verify",
    method: "POST",
    auth: "none",
    body,
  });
}

/** Idempotent by email. A returning user gets `created: false` and the SAME address. */
export function findOrCreateWallet(email: string): Promise<AuthWalletResponse> {
  return request<AuthWalletResponse>({
    base: "trade",
    path: "/auth/wallet",
    method: "POST",
    auth: "none",
    body: { email },
  });
}

/** Must be called AFTER `findOrCreateWallet` — login resolves the sub-org by email and 400s if none exists. */
export function otpLogin(body: OtpLoginRequest): Promise<OtpLoginResponse> {
  return request<OtpLoginResponse>({
    base: "trade",
    path: "/auth/otp/login",
    method: "POST",
    auth: "none",
    body,
  });
}

// ── session ───────────────────────────────────────────────────────────────────────────────────────────

/** Public by necessity: this is what MINTS a credential. The message it returns is human-readable and states
 *  that it cannot move funds — it is rendered in the signing prompt verbatim, because training users to
 *  approve opaque blobs is how the next phishing prompt gets signed. */
export function sessionChallenge(address: string): Promise<ChallengeResponse> {
  return request<ChallengeResponse>({
    base: "trade",
    path: "/session/challenge",
    method: "POST",
    auth: "none",
    body: { address },
  });
}

/** The nonce is ECHOED BACK: the server consumes it on presentation, pass or fail, so there is no oracle to
 *  grind against and a replayed signature is refused. */
export function createSession(args: {
  address: string;
  nonce: string;
  signature: string;
}): Promise<SessionResponse> {
  return request<SessionResponse>({
    base: "trade",
    path: "/session",
    method: "POST",
    auth: "none",
    body: args,
  });
}

/** The liveness read AND the `visibilitychange:visible` keepalive — one indexed lookup, and it is what stops
 *  a backgrounded tab rendering as signed in for an hour after its token died. */
export function whoAmI(signal?: AbortSignal): Promise<WhoAmIResponse> {
  return request<WhoAmIResponse>({ base: "trade", path: "/session", auth: "required", signal });
}

export function destroySession(): Promise<unknown> {
  return request<unknown>({
    base: "trade",
    path: "/session",
    method: "DELETE",
    auth: "required",
  });
}

// ── vault (limit orders and DCA only — NOT wallet funding) ─────────────────────────────────────────────

/** `registered: false` is a NORMAL state, not an error: resting orders are unavailable until the two-step
 *  registration runs, and nothing else is affected. */
export function readVault(signal?: AbortSignal): Promise<VaultResponse> {
  return request<VaultResponse>({ base: "trade", path: "/vault", auth: "required", signal });
}

export function vaultChallenge(): Promise<VaultChallengeResponse> {
  return request<VaultChallengeResponse>({
    base: "trade",
    path: "/vault/challenge",
    method: "POST",
    auth: "required",
  });
}

/**
 * ⚠️ THE RESPONSE CARRIES A CREDENTIAL AND THIS IS THE ONLY PLACE IT IS EVER SEEN.
 *
 * Jupiter issues a wallet-scoped JWT for every Trigger call and our server does NOT store it — it exchanges
 * the signature and hands the token straight back. Dropping it here is not a missing optimisation: every
 * later `/deposit/build`, `/order/create`, `/orders` and cancel is refused `428` without it, and the only
 * way to get another is a fresh signature from the user.
 */
export async function registerVault(body: VaultRegisterRequest): Promise<VaultResponse> {
  const registered = await request<
    VaultResponse & { token?: string; authorisationExpiresAt?: number }
  >({
    base: "trade",
    path: "/vault/register",
    method: "POST",
    auth: "required",
    body,
  });
  if (typeof registered.token === "string" && registered.token.length > 0) {
    rememberTriggerToken(registered.address, registered.token, registered.authorisationExpiresAt);
  }
  return registered;
}

// ── quote and swap ────────────────────────────────────────────────────────────────────────────────────

/**
 * NO SESSION REQUIRED — this route is `auth: "public"` on the server, and it is the only one on `/user/v1`
 * that is. A price is what somebody looks at BEFORE deciding to trade, and a wallet prompt in front of it is
 * friction on the one step that should be free.
 *
 * ⚠️ **IT STILL SENDS THE BEARER WHEN WE HOLD ONE — `auth: "optional"`, and that is not decoration.** The
 * server reads it purely to pick which rate-limit bucket the request lands in:
 *
 *   · with a bearer   → keyed on the WALLET, **1 quote per 2 s**, and never affected by anyone else
 *   · without one     → keyed on `clientId:IP`, **1 per 3 s, burst 3**, SHARED with everyone behind that
 *                       address — an office or a mobile carrier is one bucket for everybody on it
 *
 * So dropping the header on a signed-in user would not "make it more public", it would move them into a lane
 * a stranger can exhaust. If a signed-in user ever reports `rate_limited` on quotes they alone are not
 * causing, the first thing to check is that this is still `"optional"` and not `"none"`.
 *
 * There is a SECOND bound anonymous callers meet and signed-in ones do not: the server reserves the last of
 * the shared vendor budget for sessions, so an anonymous quote can come back `429 budget_exhausted` while a
 * signed-in one on the identical body succeeds. That is deliberate — a browse-before-connect flood must
 * degrade browsing and never the builds and order polls that tell somebody whether their money moved.
 * Surface it as "busy, try again", never as "you must sign in".
 *
 * (`/swap/build` and `/deposit/build` remain SESSION-ONLY and keep their own 3 s cadence — they follow a
 * CLICK, where a quote follows a timer, so only the timer-driven one has to clear a polling interval with
 * margin. The asymmetry is the design: the moment a TRANSACTION exists it is built for a wallet.)
 *
 * ⚠️ **EVERY REQUEST IS CHARGED, INCLUDING ONE THAT FAILS OR IS REFUSED.** A vendor error, a
 * `budget_exhausted`, a malformed body — all of them spend the 2 s. (The refund this file used to describe
 * was measured to remove the limit entirely: a vendor `400` is selectable from the request body, so one
 * wallet drove 40 real vendor calls in a single window.) The practical rule for this client is unchanged and
 * now matters more: **do not retry a failed quote immediately** — the controller's own next cycle is the
 * retry, and a 429 is never auto-retried. See `../trade/quote.ts`.
 */
export function quote(body: QuoteRequestBody, signal?: AbortSignal): Promise<QuoteResponse> {
  return request<QuoteResponse>({
    base: "trade",
    path: "/quote",
    method: "POST",
    auth: "optional",
    timeoutMs: QUOTE_TIMEOUT_MS,
    body,
    signal,
  });
}

/** NO `owner`/`taker` in the body — see rule 1. `requestId` in the response is the vendor's, and it is what
 *  makes a double-submit one order instead of two.
 *
 *  ⚠️ IT CARRIES ITS OWN PER-WALLET CADENCE — 1 per 3 s, burst 2, separate from the quote's — because it
 *  returns the same routed price plus a transaction. Burst 2 covers the honest retry ("insufficient funds",
 *  fix it, click again); a THIRD build in 3 s is a `429` and must be shown, not retried. */
export function buildSwap(body: Omit<SwapBuildRequest, "owner">): Promise<SwapBuildResponse> {
  return request<SwapBuildResponse>({
    base: "trade",
    path: "/swap/build",
    method: "POST",
    auth: "required",
    timeoutMs: BUILD_TIMEOUT_MS,
    body,
  });
}

/**
 * SUBMIT — ONE call for every kind, against the ONE route that takes a signed transaction.
 *
 * `/swap/submit` and `/withdraw/submit` still exist as alias URLs on the server, so this could equally send
 * either. It sends `/submit` because a client with one submit function and two paths is one edit away from
 * the asymmetry this consolidation removed on the server side.
 *
 * `id` is the canonical spelling: a swap's is the `requestId` from `/swap/build`, a transfer's is the `id`
 * from `/transfer/build`. Sending more than one spelling is a 400.
 */
export function submit(args: { id: string; signedTransaction: string }): Promise<SubmitResponse> {
  return request<SubmitResponse>({
    base: "trade",
    path: "/submit",
    method: "POST",
    auth: "required",
    timeoutMs: SUBMIT_TIMEOUT_MS,
    body: args,
  });
}

// ── resting orders (limit / DCA) ──────────────────────────────────────────────────────────────────────

/** ⚠️ THIS IS A **VAULT** DEPOSIT — money LEAVING the wallet into a custodial escrow — and it is a different
 *  word from funding the wallet on purpose. A UI that calls both "deposit" is how a user signs the second
 *  thinking it is the first. */
export function buildVaultDeposit(
  body: DepositBuildRequest,
  owner?: string,
): Promise<DepositBuildResponse> {
  return request<DepositBuildResponse>({
    base: "trade",
    path: "/deposit/build",
    method: "POST",
    auth: "required",
    ...(owner === undefined ? {} : { triggerOwner: owner }),
    timeoutMs: BUILD_TIMEOUT_MS,
    body,
  });
}

/** ⚠️ A CREATE IS A **SUBMIT**, NOT A BUILD, AND IT GETS THE SUBMIT BUDGET. It hands jupiter a signed
 *  deposit which jupiter then broadcasts, so it is a vendor round trip PLUS a chain write — the same shape
 *  as `submitSwap`, and the only one of the three that can move money. MEASURED 2026-09-16: on the 20 s
 *  build budget the server's own 15 s vendor abort fired first and the user was told their deposit "may
 *  have landed without an order against it"; it had not. Whoever times out first owns the answer, and the
 *  SERVER is the only party that can watch the deposit afterwards — so its budget must be the shorter one. */
export function createOrder(
  body: OrderCreateRequest,
  owner?: string,
): Promise<OrderCreateResponse> {
  return request<OrderCreateResponse>({
    base: "trade",
    path: "/order/create",
    method: "POST",
    auth: "required",
    ...(owner === undefined ? {} : { triggerOwner: owner }),
    timeoutMs: ORDER_CREATE_TIMEOUT_MS,
    body,
  });
}

/** ⚠️ `owner` IS NOT OPTIONAL IN PRACTICE — IT IS WHAT ATTACHES THE `X-Trigger-Token`. MEASURED 2026-09-17:
 *  these two were the only order calls that did not pass it, so every cancel went out with no vendor
 *  authorisation and the server answered `428` twice while the browser sat on a spinner. The list beside it
 *  loaded fine, which is what made it look like a UI fault rather than a missing header — **and cancel is
 *  the only path that gets funds back out of a vault.** */
export function cancelOrder(
  id: string,
  kind: OrderKind,
  owner?: string,
): Promise<OrderCancelResponse> {
  return request<OrderCancelResponse>({
    base: "trade",
    path: `/order/${encodeURIComponent(id)}/cancel`,
    method: "POST",
    auth: "required",
    ...(owner === undefined ? {} : { triggerOwner: owner }),
    body: { kind },
  });
}

export function confirmCancelOrder(
  id: string,
  body: OrderCancelConfirmRequest,
  owner?: string,
): Promise<OrderCancelConfirmResponse> {
  return request<OrderCancelConfirmResponse>({
    base: "trade",
    path: `/order/${encodeURIComponent(id)}/cancel/confirm`,
    method: "POST",
    auth: "required",
    ...(owner === undefined ? {} : { triggerOwner: owner }),
    body,
  });
}

/** VENDOR-POLLED: a resting order emits no on-chain event until it fills, so there is nothing to stream.
 *  ONCE PER VIEW — it shares the vendor budget with quotes. */
export function listOrders(
  kind: OrderKind,
  signal?: AbortSignal,
  owner?: string,
): Promise<OrdersResponse> {
  return request<OrdersResponse>({
    base: "trade",
    path: "/orders",
    params: { kind, ...(owner === undefined ? {} : { owner }) },
    auth: "required",
    ...(owner === undefined ? {} : { triggerOwner: owner }),
    signal,
  });
}

// ── transfer (wallet → anywhere; "withdraw" was the old name) ──────────────────────────────────────────────────────────────────────────────────────────

/** The wire takes a MINT (absent ⇒ native SOL). The panel above it still offers a CLOSED LIST of assets, and
 *  maps the choice to a mint here — because native and wrapped SOL share a mint, so a mint field alone cannot
 *  say which the user meant, and the place to resolve that ambiguity is the UI that knows. */
export function buildTransfer(
  body: Omit<TransferBuildRequest, "owner">,
): Promise<TransferBuildResponse> {
  return request<TransferBuildResponse>({
    base: "trade",
    path: "/transfer/build",
    method: "POST",
    auth: "required",
    timeoutMs: BUILD_TIMEOUT_MS,
    body,
  });
}

// ── the intent log ────────────────────────────────────────────────────────────────────────────────────

/** Everything we BUILT for this wallet and what became of it. There is no `?owner=`: on-chain positions are
 *  public and the read surface serves them by address, but what we built for someone — and what they
 *  ABANDONED — is not. */
export function listIntents(signal?: AbortSignal): Promise<IntentsResponse> {
  return request<IntentsResponse>({ base: "trade", path: "/intents", auth: "required", signal });
}

/**
 * CLOSE A BUILD THE BROWSER FAILED TO CARRY THROUGH.
 *
 * Signing happens in the browser, so a failure there is otherwise INVISIBLE: the server logs a successful
 * build and then simply never sees a submit. Without this a user who cancelled a prompt is indistinguishable
 * from a deposit that moved money and lost its order — and the second one raises an alert.
 *
 * The message is bounded HERE. An exception string is unbounded text on a money surface, and a label taken
 * verbatim from a request body is unbounded series creation as a service.
 */
export function report(body: ReportRequest): Promise<unknown> {
  return request<unknown>({
    base: "trade",
    path: "/report",
    method: "POST",
    auth: "required",
    body: { ...body, ...(body.error ? { error: body.error.slice(0, 200) } : {}) },
  });
}
